/**
 * Sinistres data service (Phase 5) — fiches 22736 (liste), 22989 (recherche /
 * statut de la liste), 22807 (détail), 22740 (intervenants), 22742
 * (charges), 22744 (documents), 22746 (pièces).
 *
 * The list (`WS2_EA_TAB_SINISTRES`) is filtered server-side by `q` (free
 * text) and `etat` (a `VAL` of `WS2_EA_SEL_STATUT_SINISTRE`: `CLOS` /
 * `OUVERT`), each sent only when non-blank (« Tous statuts » ⇒ no `etat`).
 *
 * Every call goes through the ERP proxy helpers (`./erpProxy`), which unwrap
 * the envelope and throw an `Error` carrying the ERP message. Parameters are
 * cased exactly as in the fiches (VEOS is case-sensitive): `id_sin` for
 * `WS2_EA_FIC_SIN` / `WS2_EA_TAB_INTERV_FIC_SIN`, `ID_SIN` for
 * `WS2_EA_TAB_SINGAR` / `WS2_EA_TAB_DOCS_SIN` / `WS2_EA_TAB_SIN_PMQ`. `SES`
 * is never sent (the spoof interceptor sends `X-Erp-Session-Id`).
 *
 * Raw VEOS rows are mapped to the view models the claim cards render:
 * texts trimmed (`null` / blank ⇒ `null`), amounts coerced to numbers
 * (`null` kept — "null renders nothing"), dates kept raw for the list (the
 * DataList date renderer formats them and sorts chronologically) and
 * pre-formatted `JJ/MM/AAAA` for the documents / pieces cards.
 */

import type { DocumentItem } from '../components/shared/documentItems';
import { buildPieceUploadTarget, type PieceItem } from '../components/shared/pieceItems';
import { normaliseStatus } from '../components/shared/statusBadgeConfig';
import { formatVeosDate, toFiniteNumber, type VeosScalar } from '../utils/format';
import { fetchErpList, fetchErpRecord, fetchErpRows, type ErpList } from './erpProxy';
import { buildFilterParams, fetchSelOptions, type SelOption } from './listFilters';

/** Endpoint slugs of the Sinistres screens (contract §6). */
export const CLAIM_ENDPOINTS = {
  list: 'boby-ws2-ea-tab-sinistres',
  statusOptions: 'boby-ws2-ea-sel-statut-sinistre',
  record: 'boby-ws2-ea-fic-sin',
  stakeholders: 'boby-ws2-ea-tab-interv-fic-sin',
  charges: 'boby-ws2-ea-tab-singar',
  documents: 'boby-ws2-ea-tab-docs-sin',
  pieces: 'boby-ws2-ea-tab-sin-pmq',
} as const;

/** A claim id as carried by the route (`:idSin`) or a VEOS row. */
export type ClaimId = string | number;

// ---------------------------------------------------------------------------
// Raw VEOS rows (only the fields the screens use are required)
// ---------------------------------------------------------------------------

/** One `WS2_EA_TAB_SINISTRES` row (fiche 22736). */
export interface ClaimListRaw {
  ID_SIN: number | string | null;
  ASSURE: string | null;
  NUM_POL: string | null;
  LIB_BR: string | null;
  NCBT_SIN: string | null;
  LIB_CIRC: string | null;
  DATS_SIN: number | string | null;
  ETAT: string | null;
  COUT_ESTIME: number | string | null;
  ID_POL?: number | null;
  ID_PER?: number | null;
  ID_NPOL?: number | null;
  LIB_MVT?: string | null;
  LIB_MOT?: string | null;
  LIB_ETAT?: string | null;
  TAUX_RESP?: string | null;
}

/** The `WS2_EA_FIC_SIN` record (fiche 22807). */
export interface ClaimRecordRaw {
  NOM_ASSURE: string | null;
  NCBT_SIN: string | null;
  LIB_MOT: string | null;
  TYPE_SIN: string | null;
  LIGN_SDM: string | null;
  TAUX_RESP: string | null;
  LIGN_SCS: string | null;
  ETAT: string | null;
  LIB_CIRC: string | null;
  DATS_SIN: number | string | null;
  DATD_SIN: number | string | null;
  TOTAL_CIE: number | string | null;
  LIB_PROD: string | null;
  NCIE_SIN?: string | null;
  NOM2_PER?: string | null;
  LIB_ETAT?: string | null;
}

/** One `WS2_EA_TAB_INTERV_FIC_SIN` row (fiche 22740). */
export interface ClaimStakeholderRaw {
  CD_CATEG: string | null;
  NOM_PRENOM_PER: string | null;
  TELPRINC_PER: string | null;
  EMAIL_PER: string | null;
  TYPE?: string | null;
  NOM_PER?: string | null;
  PRENOM_PER?: string | null;
  TELPORT_PER?: string | null;
}

/** One `WS2_EA_TAB_SINGAR` row (fiche 22742). */
export interface ClaimChargeRaw {
  ID_SGAR: number | string | null;
  LIB_GAR: string | null;
  PROV_RESTANTE: number | string | null;
  REG: number | string | null;
  RECPROV: number | string | null;
  RECREG: number | string | null;
  FRANCH_SGAR: number | string | null;
  ID_SIN?: number | null;
  CD_GAR?: string | null;
  /** Not displayed — « Provision » is fed by `PROV_RESTANTE`. */
  PROV?: number | null;
}

/** One `WS2_EA_TAB_DOCS_SIN` row (fiche 22744). */
export interface ClaimDocumentRaw {
  ID_ADOC: number | string | null;
  LIB_ADOC: string | null;
  DAT_ADOC: number | string | null;
  LIB_DS: string | null;
  ORIGINE?: string | null;
}

/** One `WS2_EA_TAB_SIN_PMQ` row (fiche 22746). */
export interface ClaimPieceRaw {
  ID_PMD: number | string | null;
  LIB_PIE: string | null;
  DATDEMANDE_PMD: number | string | null;
  STATUT: string | null;
  ID_SIN?: number | string | null;
  NUM_POL?: string | null;
  /** Document category of the piece — the upload `type` (fiches 22746 / 22976). */
  CD_DS?: string | null;
}

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

/** One claim of « Mes sinistres » (`extranet.claimsListCard` row). */
export interface ClaimListRow {
  /** Stable row id (`ID_SIN`, else the row position). */
  id: string;
  /** Claim id used to open the detail (`ID_SIN`); `null` ⇒ not openable. */
  idSin: string | null;
  /** Insured (`ASSURE`). */
  assure: string | null;
  /** Product / branch (`LIB_BR`), as returned. */
  produit: string | null;
  /** Contract number (`NUM_POL`). */
  numeroContrat: string | null;
  /** Claim reference (`NCBT_SIN`). */
  reference: string | null;
  /** Occurrence date — raw ms timestamp (`DATS_SIN`). */
  dateSurvenance: number | string | null;
  /** Circumstances (`LIB_CIRC`). */
  circonstances: string | null;
  /** Estimated insurer cost in euros (`COUT_ESTIME`). */
  coutAssureur: number | null;
  /** Status (`ETAT`), normalised: `OUVERT` / `CLOS`. */
  statut: string | null;
}

/** The claim record (`WS2_EA_FIC_SIN`) — header, details tab and rule context. */
export interface ClaimRecord {
  /** Insured name (`NOM_ASSURE`) — page title. */
  nomAssure: string | null;
  /** Claim reference (`NCBT_SIN`). */
  reference: string | null;
  /** Status label (`LIB_MOT`). */
  libelleStatut: string | null;
  /** Computed status (`ETAT`), normalised: `OUVERT` / `CLOS`. */
  etat: string | null;
  /** Product (`LIB_PROD`), as returned. */
  produit: string | null;
  /** Claim type (`TYPE_SIN`), verbatim. */
  type: string | null;
  /** Responsibility rate (`TAUX_RESP`, e.g. « 0% », « Non déterminé »). */
  tauxResponsabilite: string | null;
  /** Occurrence date — raw ms timestamp (`DATS_SIN`). */
  dateSurvenance: number | string | null;
  /** Declaration date — raw ms timestamp (`DATD_SIN`). */
  dateDeclaration: number | string | null;
  /** Insurer cost in euros (`TOTAL_CIE`). */
  coutAssureur: number | null;
  /** Circumstances (`LIB_CIRC`). */
  circonstances: string | null;
  /** Description of the cause / course of events (`LIGN_SCS`). */
  description: string | null;
  /** Description of the damages (`LIGN_SDM`). */
  dommages: string | null;
}

/** One stakeholder of the claim (`extranet.claimStakeholdersCard` row). */
export interface ClaimStakeholderRow {
  /** Stable row id (row position — VEOS returns no person id). */
  id: string;
  /** Nature (`CD_CATEG`). */
  nature: string | null;
  /** Name (`NOM_PRENOM_PER` — never `NOM_PER`). */
  nomPrenom: string | null;
  /** E-mail (`EMAIL_PER`). */
  email: string | null;
  /** Phone (`TELPRINC_PER` — never `TELPORT_PER`). */
  telephone: string | null;
}

/** One guarantee line of the claim charges (`extranet.claimChargesCard` row). */
export interface ClaimChargeRow {
  /** Stable row id (`ID_SGAR`, else the row position). */
  id: string;
  /** Guarantee (`LIB_GAR`). */
  garantie: string | null;
  /** Provision — fed by `PROV_RESTANTE` (never `PROV`). */
  provision: number | null;
  /** Settlement (`REG`). */
  reglement: number | null;
  /** Estimated recovery (`RECPROV`). */
  recoursEstime: number | null;
  /** Collected recovery (`RECREG`). */
  recoursEncaisse: number | null;
  /** Deductible (`FRANCH_SGAR`). */
  franchise: number | null;
}

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** Trimmed text, `null` when missing or blank. */
function textOrNull(value: VeosScalar): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

/** A text value exactly as returned (no trim / case change); missing or empty ⇒ `null`. */
function verbatimOrNull(value: string | null | undefined): string | null {
  return value === undefined || value === null || value === '' ? null : value;
}

/** A VEOS id as a string, `null` when missing. */
function idOrNull(value: VeosScalar): string | null {
  return textOrNull(value);
}

/** Normalised status value (`" ouvert "` → `OUVERT`), `null` when blank. */
function statusOrNull(value: VeosScalar): string | null {
  const text = textOrNull(value);
  return text === null ? null : normaliseStatus(text);
}

/** A raw VEOS date (ms timestamp or pre-formatted string) kept as-is; blank ⇒ `null`. */
function rawDate(value: number | string | null | undefined): number | string | null {
  if (typeof value === 'number') return value;
  return textOrNull(value);
}

/** A VEOS date pre-formatted `JJ/MM/AAAA` (Paris calendar day); `null` when missing. */
function formattedDate(value: VeosScalar): string | null {
  const text = formatVeosDate(value);
  return text === '' ? null : text;
}

export function toClaimListRow(raw: ClaimListRaw, index: number): ClaimListRow {
  const idSin = idOrNull(raw.ID_SIN);
  return {
    id: idSin ?? `row-${String(index)}`,
    idSin,
    assure: textOrNull(raw.ASSURE),
    produit: textOrNull(raw.LIB_BR),
    numeroContrat: textOrNull(raw.NUM_POL),
    reference: textOrNull(raw.NCBT_SIN),
    dateSurvenance: rawDate(raw.DATS_SIN),
    circonstances: textOrNull(raw.LIB_CIRC),
    coutAssureur: toFiniteNumber(raw.COUT_ESTIME),
    statut: statusOrNull(raw.ETAT),
  };
}

export function toClaimRecord(raw: ClaimRecordRaw): ClaimRecord {
  return {
    nomAssure: textOrNull(raw.NOM_ASSURE),
    reference: textOrNull(raw.NCBT_SIN),
    libelleStatut: textOrNull(raw.LIB_MOT),
    etat: statusOrNull(raw.ETAT),
    produit: textOrNull(raw.LIB_PROD),
    // « Afficher exactement la valeur TYPE_SIN » — no trim, no case change.
    type: verbatimOrNull(raw.TYPE_SIN),
    tauxResponsabilite: textOrNull(raw.TAUX_RESP),
    dateSurvenance: rawDate(raw.DATS_SIN),
    dateDeclaration: rawDate(raw.DATD_SIN),
    coutAssureur: toFiniteNumber(raw.TOTAL_CIE),
    circonstances: textOrNull(raw.LIB_CIRC),
    description: textOrNull(raw.LIGN_SCS),
    dommages: textOrNull(raw.LIGN_SDM),
  };
}

export function toClaimStakeholderRow(raw: ClaimStakeholderRaw, index: number): ClaimStakeholderRow {
  return {
    id: String(index),
    nature: textOrNull(raw.CD_CATEG),
    nomPrenom: textOrNull(raw.NOM_PRENOM_PER),
    email: textOrNull(raw.EMAIL_PER),
    telephone: textOrNull(raw.TELPRINC_PER),
  };
}

export function toClaimChargeRow(raw: ClaimChargeRaw, index: number): ClaimChargeRow {
  return {
    id: idOrNull(raw.ID_SGAR) ?? `row-${String(index)}`,
    garantie: textOrNull(raw.LIB_GAR),
    provision: toFiniteNumber(raw.PROV_RESTANTE),
    reglement: toFiniteNumber(raw.REG),
    recoursEstime: toFiniteNumber(raw.RECPROV),
    recoursEncaisse: toFiniteNumber(raw.RECREG),
    franchise: toFiniteNumber(raw.FRANCH_SGAR),
  };
}

export function toClaimDocument(raw: ClaimDocumentRaw, index: number): DocumentItem {
  return {
    id: idOrNull(raw.ID_ADOC) ?? `row-${String(index)}`,
    categorie: textOrNull(raw.LIB_DS),
    nom: textOrNull(raw.LIB_ADOC),
    date: formattedDate(raw.DAT_ADOC),
  };
}

/**
 * Map one `WS2_EA_TAB_SIN_PMQ` row. `upload` carries the ids of the piece
 * upload (fiche 22976): `idTab` = call 2 path `ID_SIN`, `ID_PMD`, `type` =
 * `CD_DS` — `null` when one is missing.
 */
export function toClaimPiece(raw: ClaimPieceRaw, index: number): PieceItem {
  const idSin = toFiniteNumber(raw.ID_SIN);
  return {
    id: idOrNull(raw.ID_PMD) ?? `row-${String(index)}`,
    libelle: textOrNull(raw.LIB_PIE),
    dateDemande: formattedDate(raw.DATDEMANDE_PMD),
    statut: textOrNull(raw.STATUT),
    upload: buildPieceUploadTarget('sinistre', {
      idPmd: toFiniteNumber(raw.ID_PMD),
      idTab: idSin,
      parentId: idSin,
      cdDs: textOrNull(raw.CD_DS),
    }),
  };
}

// ---------------------------------------------------------------------------
// List filters (fiche 22989)
// ---------------------------------------------------------------------------

/** Server-side filters of « Mes sinistres »: both omitted from the request when blank. */
export interface ClaimsListFilters {
  /** Free-text search (réf, circonstances, assuré…); blank ⇒ not sent. */
  q?: string | null;
  /** `CLOS` / `OUVERT` (`VAL` of `WS2_EA_SEL_STATUT_SINISTRE`); blank ⇒ « Tous statuts ». */
  etat?: string | null;
}

/** Request params of `WS2_EA_TAB_SINISTRES`: `q` / `etat` only when non-blank (trimmed). */
export function buildClaimsListParams(filters: ClaimsListFilters = {}): Record<string, string> {
  return buildFilterParams({ q: filters.q, etat: filters.etat });
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

/**
 * « Mes sinistres » — the claims of the session matching `q` / `etat` (each
 * sent only when non-blank), in API order, with the VEOS total.
 */
export async function fetchClaims(
  registrySlug: string | null | undefined,
  filters: ClaimsListFilters = {},
): Promise<ErpList<ClaimListRow>> {
  const list = await fetchErpList<ClaimListRaw>(
    registrySlug,
    CLAIM_ENDPOINTS.list,
    buildClaimsListParams(filters),
  );
  return { rows: list.rows.map(toClaimListRow), total: list.total };
}

/** Options of the status select (`WS2_EA_SEL_STATUT_SINISTRE`), API order. */
export async function fetchClaimStatusOptions(
  registrySlug: string | null | undefined,
): Promise<SelOption[]> {
  return fetchSelOptions(registrySlug, CLAIM_ENDPOINTS.statusOptions);
}

/** The claim record, or `null` when VEOS returned none. */
export async function fetchClaim(
  registrySlug: string | null | undefined,
  idSin: ClaimId,
): Promise<ClaimRecord | null> {
  const raw = await fetchErpRecord<ClaimRecordRaw>(registrySlug, CLAIM_ENDPOINTS.record, {
    id_sin: idSin,
  });
  return raw === null ? null : toClaimRecord(raw);
}

/** The claim's stakeholders, in API order. */
export async function fetchClaimStakeholders(
  registrySlug: string | null | undefined,
  idSin: ClaimId,
): Promise<ClaimStakeholderRow[]> {
  const rows = await fetchErpRows<ClaimStakeholderRaw>(
    registrySlug,
    CLAIM_ENDPOINTS.stakeholders,
    { id_sin: idSin },
  );
  return rows.map(toClaimStakeholderRow);
}

/** The claim's charges per guarantee, in API order. */
export async function fetchClaimCharges(
  registrySlug: string | null | undefined,
  idSin: ClaimId,
): Promise<ClaimChargeRow[]> {
  const rows = await fetchErpRows<ClaimChargeRaw>(registrySlug, CLAIM_ENDPOINTS.charges, {
    ID_SIN: idSin,
  });
  return rows.map(toClaimChargeRow);
}

/** The claim's documents, in API order (sorted by category by VEOS). */
export async function fetchClaimDocuments(
  registrySlug: string | null | undefined,
  idSin: ClaimId,
): Promise<DocumentItem[]> {
  const rows = await fetchErpRows<ClaimDocumentRaw>(registrySlug, CLAIM_ENDPOINTS.documents, {
    ID_SIN: idSin,
  });
  return rows.map(toClaimDocument);
}

/** The pieces requested for the claim, in API order. */
export async function fetchClaimPieces(
  registrySlug: string | null | undefined,
  idSin: ClaimId,
): Promise<PieceItem[]> {
  const rows = await fetchErpRows<ClaimPieceRaw>(registrySlug, CLAIM_ENDPOINTS.pieces, {
    ID_SIN: idSin,
  });
  return rows.map(toClaimPiece);
}
